// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { Utils } from '../../utils';
import { HceBed } from '../../../../../interfaces/hce/hce-annotation';

@Injectable({ providedIn: 'root' })
export class HceBedParserService {

  public parse(text: string): HceBed[] {
    const records = Utils.tsv.parse(text);
    if (records === null) {
      return [];
    }
    return records
      .filter(cols => !cols[0].startsWith('#'))
      .map(cols => ({
        refName: (cols[0] ?? '').trim(),
        refStartCutPosition: this.toInt((cols[1] ?? '').trim()),
        refEndCutPosition: this.toInt((cols[2] ?? '').trim()),
        svId: (cols[6] ?? '').trim(),
        info: (cols[10] ?? '').trim(),
        color: String(cols[11] ?? '#dddddd').trim(),
        conLength: this.toInt((cols[12] ?? '').trim()),
        refLength: this.toInt((cols[13] ?? '').trim()),
        length: this.toInt((cols[14] ?? '').trim()),
      }))
      .map(cols => ({ ...cols, type: this.toType(cols.info) }))
      .filter(({ type }) => type !== false)
      .map(cols => ({
        noOfChr: Utils.chromosome.toId(cols.refName) ?? 0,
        chromosome: cols.refName ?? '',
        svId: cols.svId,
        start: (cols.refStartCutPosition + cols.refEndCutPosition - cols.refLength) / 2,
        end: (cols.refStartCutPosition + cols.refEndCutPosition + cols.refLength) / 2,
        type: cols.type as any,
        zygosity: this.toZygosity(cols.info),
        sVConSize: cols.conLength,
        sVRefSize: cols.refLength,
        length: cols.length,
        color: cols.color,
      }));
  }

  private toValues(input: string, key: string): string[] {
    return input
      .split(';')
      .map(p => p.trim())
      .filter(Boolean)
      .map(p => {
        const eq = p.indexOf('=');
        if (eq === -1) {
          return [p.trim(), undefined as unknown as string];
        }
        const name = p.slice(0, eq).trim();
        const value = p.slice(eq + 1).trim();
        return [name, value] as const;
      })
      .filter(([name, value]) => name !== undefined && value !== undefined)
      .filter(([name]) => name === key)
      .map(([, value]) => value);
  }

  private toType(input: string): string | string[] | boolean {
    if (!input.includes('TYPE')) {
      return input;
    }
    const out = this.toValues(input, 'TYPE');
    if (out.length === 0) {
      return false;
    }
    return out.length === 1 ? out[0] : out;
  }

  private toZygosity(input: string): string {
    if (!input.includes('TYPE')) {
      return '-';
    }
    return this.toValues(input, 'ZS')[0] ?? '-';
  }

  private toInt(input: string): number {
    const n = Number(input);
    if (Number.isFinite(n)) {
      if (input.includes('.')) {
        return Utils.number.convNum(input, 0);
      }
      return n;
    }
    return 0;
  }
}
